import React from 'react';
import entete from '../../../../assets/img/entete_pool.png';
import { frenchMonth } from '../../../../variables/functions';

/**
 * Tableau d'impression d'un bordereau de cession avec en-tête et pied de page
 * COMPACTS RÉPÉTÉS SUR CHAQUE PAGE. Astuce : en impression HTML, le navigateur
 * rejoue automatiquement le <thead> en haut et le <tfoot> en bas de chaque page.
 * On y place donc l'en-tête (logos + titre + colonnes) et le pied (coordonnées),
 * volontairement légers pour économiser l'espace.
 *
 * @param {Object}   props
 * @param {string}   props.titre        Titre du bordereau (majuscules).
 * @param {Object}   props.categorie    { codeCategorie }
 * @param {Object}   props.compagnie    { raisonSociale }
 * @param {string}   props.periode      « AAAA-MM »
 * @param {string}   [props.logoPreviewUrl] Logo de la compagnie.
 * @param {Array}    props.columns      [{ title, align?, get:(item)=>node }]
 * @param {Array}    props.data         Lignes.
 * @param {string}   props.rowKey       Clé de ligne (défaut 'key').
 * @param {node}     props.totalValue   Valeur affichée dans la ligne TOTAL.
 */
const BordereauPrintTable = ({
    titre, categorie, compagnie, periode, logoPreviewUrl,
    columns = [], data = [], rowKey = 'key', totalValue,
    // OPTIONNEL : total PAR COLONNE (tableau aligné sur `columns`, une entrée par colonne,
    // '' pour les colonnes sans total). Prioritaire sur `totalValue`. Rétrocompatible : les
    // bordereaux qui ne passent qu'un total unique gardent l'ancien rendu.
    totalRow,
}) => {
    const n = columns.length;
    const moisLabel = periode ? `${frenchMonth(periode.split('-')[1]).toUpperCase()} ${periode.split('-')[0]}` : '';
    const th = { border: '1px solid #ddd', padding: '5px 8px', fontSize: '11px' };
    const td = { border: '1px solid #ddd', padding: '4px 8px', fontSize: '11px' };

    return (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            {/* EN-TÊTE — répété en haut de chaque page */}
            <thead style={{ display: 'table-header-group' }}>
                {/* Bandeau logos + titre (compact) */}
                <tr>
                    <th colSpan={n} style={{ border: 'none', padding: '0 0 6px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            {logoPreviewUrl
                                ? <img src={logoPreviewUrl} alt="" style={{ height: '34px', objectFit: 'contain' }} />
                                : <span />}
                            <img src={entete} alt="" style={{ height: '34px', objectFit: 'contain', marginLeft: 'auto' }} />
                        </div>
                        <div style={{ borderTop: '3px solid #14356a', margin: '3px 0 5px' }} />
                        <div style={{ textAlign: 'center' }}>
                            <div style={{ fontSize: '12.5px', fontWeight: 'bold' }}>
                                {titre}{moisLabel ? ` DU MOIS DE ${moisLabel}` : ''}
                            </div>
                            <div style={{ fontSize: '10.5px', color: '#333' }}>
                                Catégorie {categorie?.codeCategorie || ''}
                                {compagnie?.raisonSociale ? ` — ${compagnie.raisonSociale}` : ''}
                            </div>
                        </div>
                    </th>
                </tr>
                {/* En-têtes de colonnes (répétés aussi) */}
                <tr style={{ backgroundColor: '#f0f0f0' }}>
                    {columns.map((c, i) => (
                        <th key={i} style={{ ...th, textAlign: c.align || 'left' }}>{c.title}</th>
                    ))}
                </tr>
            </thead>

            {/* PIED — répété en bas de chaque page */}
            <tfoot style={{ display: 'table-footer-group' }}>
                <tr>
                    <td colSpan={n} style={{ border: 'none', paddingTop: '8px' }}>
                        <div style={{ borderTop: '2px solid #14356a', paddingTop: '4px', textAlign: 'center', fontSize: '9px', color: '#14356a' }}>
                            POOL TPVM-VT GIE — Tél : +228 92-62-66-66 | WhatsApp : +228 70-48-11-11 | Télécopie : +228 22-20-55-80 | N°RC : 2009 C 0771 | N°NIF : 1000 16 1028
                            <div>Entreprises régies par le code des assurances</div>
                        </div>
                    </td>
                </tr>
            </tfoot>

            <tbody>
                {data.map((item, index) => (
                    <tr key={item[rowKey] || item.codeId || index}>
                        {columns.map((c, i) => (
                            <td key={i} style={{ ...td, textAlign: c.align || 'left' }}>{c.get(item)}</td>
                        ))}
                    </tr>
                ))}
                {Array.isArray(totalRow) ? (
                    // Total par colonne : « TOTAL » sur la 1re colonne, une valeur par colonne ensuite.
                    <tr style={{ fontWeight: 'bold', backgroundColor: '#f9f9f9' }}>
                        {columns.map((c, i) => (
                            <td key={i} style={{ ...td, textAlign: i === 0 ? 'left' : (c.align || 'right') }}>
                                {i === 0 ? 'TOTAL' : (totalRow[i] ?? '')}
                            </td>
                        ))}
                    </tr>
                ) : (
                    <tr style={{ fontWeight: 'bold', backgroundColor: '#f9f9f9' }}>
                        <td colSpan={n - 1} style={{ ...td, textAlign: 'right' }}>TOTAL</td>
                        <td style={{ ...td, textAlign: 'right' }}>{totalValue}</td>
                    </tr>
                )}
                {/* Signature (n'apparaît qu'en fin de tableau, pas répétée) */}
                <tr>
                    <td colSpan={n} style={{ border: 'none', paddingTop: '30px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px' }}>
                            <div>Date : {new Date().toLocaleDateString('fr-FR')}</div>
                            <div style={{ textAlign: 'center' }}>
                                <div style={{ marginBottom: '45px' }}>Cachet et Signature</div>
                                <div style={{ borderTop: '1px solid #000', paddingTop: '4px', minWidth: '180px' }}>
                                    {compagnie?.raisonSociale || ''}
                                </div>
                            </div>
                        </div>
                    </td>
                </tr>
            </tbody>
        </table>
    );
};

export default BordereauPrintTable;
